import type { Change, LogEntry, LogSource, LogType, MessageOrigin } from './types.js';
import type { NotionPage, NotionWriteProperties } from './notion-types.js';
import { plainText, richText } from './schema.js';

/**
 * CRM Log (§4.3, D-5): the app-owned data source created inside the CRM database.
 * Property names are fixed here; the server creates the table from this definition
 * and both ends map pages with the functions below.
 */
export const LOG_TITLE = 'CRM Log';

export const LOG_TYPES: LogType[] = ['note', 'create', 'update', 'follow-up', 'snooze', 'archive', 'restore', 'suggestion', 'external', 'revert', 'message'];
export const MESSAGE_ORIGINS: MessageOrigin[] = ['ai', 'ai-edited', 'manual'];
export const LOG_SOURCES: LogSource[] = ['app', 'notion', 'system', 'telegram'];

export const LOG_PROPS = {
  name: 'Name',
  contact: 'Contact',
  type: 'Type',
  actor: 'Actor',
  actorEmail: 'Actor Email',
  at: 'At',
  text: 'Text',
  changes: 'Changes',
  actionId: 'Action ID',
  reverts: 'Reverts',
  reverted: 'Reverted',
  source: 'Source',
  conflict: 'Conflict',
  origin: 'Origin',
} as const;

/** Property definitions for `POST /v1/data_sources` (everything but the self-relation). */
export function logDataSourceProperties(contactsDataSourceId: string): Record<string, unknown> {
  return {
    [LOG_PROPS.name]: { title: {} },
    [LOG_PROPS.contact]: { relation: { data_source_id: contactsDataSourceId, type: 'single_property', single_property: {} } },
    [LOG_PROPS.type]: { select: { options: LOG_TYPES.map((name) => ({ name, color: TYPE_COLOR[name] })) } },
    [LOG_PROPS.actor]: { people: {} },
    [LOG_PROPS.actorEmail]: { email: {} },
    [LOG_PROPS.at]: { date: {} },
    [LOG_PROPS.text]: { rich_text: {} },
    [LOG_PROPS.changes]: { rich_text: {} },
    [LOG_PROPS.actionId]: { rich_text: {} },
    [LOG_PROPS.reverted]: { checkbox: {} },
    [LOG_PROPS.source]: { select: { options: LOG_SOURCES.map((name) => ({ name })) } },
    [LOG_PROPS.conflict]: { checkbox: {} },
    [LOG_PROPS.origin]: { select: { options: MESSAGE_ORIGINS.map((name) => ({ name })) } },
  };
}

/** Columns added after the table first shipped; `ensureLogDataSource` adds any that are missing. */
export function logMissingProperties(existing: Record<string, unknown>, contactsDataSourceId: string): Record<string, unknown> {
  const all = logDataSourceProperties(contactsDataSourceId);
  const out: Record<string, unknown> = {};
  for (const [name, def] of Object.entries(all)) if (!(name in existing)) out[name] = def;
  return out;
}

/** The `Reverts` self-relation, added with a second request once the data source id is known. */
export function logSelfRelationProperties(logDataSourceId: string): Record<string, unknown> {
  return {
    [LOG_PROPS.reverts]: { relation: { data_source_id: logDataSourceId, type: 'single_property', single_property: {} } },
  };
}

const TYPE_COLOR: Record<LogType, string> = {
  note: 'blue',
  create: 'green',
  update: 'default',
  'follow-up': 'purple',
  snooze: 'yellow',
  archive: 'red',
  restore: 'green',
  suggestion: 'pink',
  external: 'orange',
  revert: 'brown',
  message: 'blue',
};

/** Notion page in CRM Log → canonical entry. Tolerates missing properties (older rows). */
export function logFromPage(page: NotionPage): LogEntry {
  const props = page.properties;
  const text = (n: string): string => {
    const p = props[n];
    if (!p) return '';
    return p.type === 'title' ? plainText(p.title) : p.type === 'rich_text' ? plainText(p.rich_text) : '';
  };
  const select = (n: string): string | null => {
    const p = props[n];
    return p && p.type === 'select' ? (p.select?.name ?? null) : null;
  };
  const relation = (n: string): string[] => {
    const p = props[n];
    return p && p.type === 'relation' ? p.relation.map((r) => r.id) : [];
  };
  const checkbox = (n: string): boolean => {
    const p = props[n];
    return p && p.type === 'checkbox' ? p.checkbox : false;
  };
  const people = props[LOG_PROPS.actor];
  const at = props[LOG_PROPS.at];
  const email = props[LOG_PROPS.actorEmail];
  let changes: Change[] = [];
  try {
    const raw = text(LOG_PROPS.changes);
    if (raw) changes = JSON.parse(raw) as Change[];
  } catch {
    changes = [];
  }
  const type = select(LOG_PROPS.type);
  const source = select(LOG_PROPS.source);
  const origin = select(LOG_PROPS.origin);
  return {
    id: page.id,
    name: text(LOG_PROPS.name),
    contactId: relation(LOG_PROPS.contact)[0] ?? null,
    type: LOG_TYPES.includes(type as LogType) ? (type as LogType) : 'note',
    actor: people && people.type === 'people' ? (people.people[0]?.id ?? null) : null,
    actorEmail: email && email.type === 'email' ? email.email : null,
    at: at && at.type === 'date' && at.date ? at.date.start : page.created_time,
    text: text(LOG_PROPS.text),
    changes,
    actionId: text(LOG_PROPS.actionId) || null,
    reverts: relation(LOG_PROPS.reverts)[0] ?? null,
    reverted: checkbox(LOG_PROPS.reverted),
    source: LOG_SOURCES.includes(source as LogSource) ? (source as LogSource) : 'app',
    conflict: checkbox(LOG_PROPS.conflict),
    origin: MESSAGE_ORIGINS.includes(origin as MessageOrigin) ? (origin as MessageOrigin) : null,
    archived: Boolean(page.archived || page.in_trash),
    lastEditedTime: page.last_edited_time,
  };
}

export type NewLogEntry = Omit<LogEntry, 'id' | 'archived' | 'lastEditedTime' | 'pending'>;

/** Canonical entry → Notion `properties` for `pages.create` in CRM Log. */
export function propertiesFromLog(e: NewLogEntry): NotionWriteProperties {
  const out: NotionWriteProperties = {
    [LOG_PROPS.name]: { title: richText(e.name.slice(0, 200)) },
    [LOG_PROPS.contact]: { relation: e.contactId ? [{ id: e.contactId }] : [] },
    [LOG_PROPS.type]: { select: { name: e.type } },
    [LOG_PROPS.actor]: { people: e.actor ? [{ object: 'user', id: e.actor }] : [] },
    [LOG_PROPS.actorEmail]: { email: e.actorEmail },
    [LOG_PROPS.at]: { date: { start: e.at } },
    [LOG_PROPS.text]: { rich_text: richText(e.text) },
    [LOG_PROPS.changes]: { rich_text: richText(e.changes.length ? JSON.stringify(e.changes) : '') },
    [LOG_PROPS.actionId]: { rich_text: richText(e.actionId ?? '') },
    [LOG_PROPS.reverts]: { relation: e.reverts ? [{ id: e.reverts }] : [] },
    [LOG_PROPS.reverted]: { checkbox: e.reverted },
    [LOG_PROPS.source]: { select: { name: e.source } },
    [LOG_PROPS.conflict]: { checkbox: e.conflict },
    [LOG_PROPS.origin]: { select: e.origin ? { name: e.origin } : null },
  };
  return out;
}

/** Entries the sync treats as changed when they differ (everything but the volatile timestamp). */
export function logEntriesDiffer(a: LogEntry, b: LogEntry): boolean {
  const strip = ({ lastEditedTime: _t, pending: _p, ...rest }: LogEntry) => rest;
  return JSON.stringify(strip(a)) !== JSON.stringify(strip(b));
}
